import {
  Flex,
  Loader,
  Text,
  Title,
  Paper,
  Badge,
  Drawer,
  Group,
  ActionIcon,
} from "@mantine/core";
import React, { useState, useMemo, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import { useQuery } from "react-query";
import { getMeetingsForMyOrg } from "api";
import EmptyCard from "../EmptyCard/EmptyCard";
import MeetingDistModal from "../MeetingDistBarCard/MeetingDistModal";
import ExternalOrgTable from "./ExternalOrgTable";
import { IconBuilding, IconClock, IconUsers } from "@tabler/icons-react";
import { formatOrgName } from "utils/insights";

const ExternalOrgTimeCard = ({
  data,
  isLoading,
  isError,
  source = "myOrg",
  duration,
  self,
  uid,
}) => {
  const [modalOpened, setModalOpened] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [drawerOpened, setDrawerOpened] = useState(false);

  const redisKey = useMemo(() => {
    if (!uid || !duration || !self || source !== "myOrg") {
      return null;
    }
    return `users:${uid}:orgCalculations:externalOrgsTime:${duration}:${self}:${modalData?.orgName}`;
  }, [uid, duration, self, source, modalData]);

  // Fetch meeting details using useQuery
  const {
    data: meetingDetails,
    isLoading: isLoadingMeetings,
    isError: isErrorMeetings,
    error: meetingError,
  } = useQuery(
    ["externalOrgMeetingDetails", redisKey],
    () => getMeetingsForMyOrg(redisKey),
    {
      enabled: modalOpened && redisKey !== null,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  const handleBarClick = useCallback(
    (params) => {
      if (source === "myOrg") {
        setModalData({
          name: `${params.name} Meetings`,
          type: "External Organization",
          orgName: params.name,
        });
        setModalOpened(true);
      }
    },
    [source]
  );

  const handleRowClick = useCallback(
    (orgName) => {
      if (source === "myOrg") {
        setModalData({
          name: `${orgName} Meetings`,
          type: "External Organization",
          orgName: orgName,
        });
        setModalOpened(true);
      }
    },
    [source]
  );

  const onEvents = useMemo(() => {
    if (source === "myOrg") {
      return { click: handleBarClick };
    }
    return {};
  }, [handleBarClick, source]);

  // Professional color palette for different organizations
  const colorPalette = [
    "#39a8f0", // Blue
    "#fab005", // Yellow
    "#51cf66", // Green
    "#ff6b6b", // Red
    "#845ef7", // Purple
    "#fd7e14", // Orange
    "#20c997", // Teal
    "#e64980", // Pink
    "#868e96", // Gray
    "#91a7ff", // Light Blue
    "#ffd43b", // Light Yellow
    "#8ce99a", // Light Green
  ];

  // Process data for chart (top 10)
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return null;

    const entries = Object.entries(data)
      .sort((a, b) => b[1].hours - a[1].hours)
      .slice(0, 10); // Show top 10 organizations

    return {
      categories: entries.map(([org]) => org),
      hours: entries.map(([, info]) => Math.ceil(info.hours || 0)),
      meetings: entries.map(([, info]) => info.meetings || 0),
      colors: entries.map(
        (_, index) => colorPalette[index % colorPalette.length]
      ),
    };
  }, [data]);

  // Process all data for drawer (no slicing)
  const fullData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return null;

    const entries = Object.entries(data).sort(
      (a, b) => b[1].hours - a[1].hours
    );

    return entries.map(([org, info], index) => ({
      organization: org,
      hours: Math.ceil(info.hours || 0),
      meetings: info.meetings || 0,
      color: colorPalette[index % colorPalette.length],
    }));
  }, [data]);

  const option = useMemo(() => {
    if (!chartData) return {};

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params) => {
          const param = params[0];
          const index = param.dataIndex;
          const orgName = formatOrgName(chartData.categories[index]);
          const hours = chartData.hours[index];
          const meetings = chartData.meetings[index];
          const barColor = chartData.colors[index];

          return `
             <div style="padding: 8px;">
               <div style="font-weight: bold; margin-bottom: 4px;">${orgName}</div>
               <div style="display: flex; align-items: center; margin-bottom: 2px;">
                 <span style="color: ${barColor}; margin-right: 4px;">●</span>
                 Time Spent: <strong>${hours} hours</strong>
               </div>
               <div style="display: flex; align-items: center;">
                 <span style="color: ${barColor}; margin-right: 4px; opacity: 0.7;">●</span>
                 Meetings: <strong>${meetings}</strong>
               </div>
             </div>
           `;
        },
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderColor: "#e9ecef",
        borderWidth: 1,
        textStyle: {
          color: "#495057",
          fontSize: 12,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: chartData.categories,
        axisLabel: {
          rotate: -45,
          fontSize: 11,
          color: "#666",
          formatter: (value) => {
            const orgName = formatOrgName(value);
            return orgName.length > 15
              ? orgName.substring(0, 15) + "..."
              : orgName;
          },
        },
        axisLine: {
          lineStyle: {
            color: "#e9ecef",
          },
        },
      },
      yAxis: {
        type: "value",
        name: "Hours",
        nameTextStyle: {
          color: "#666",
          fontSize: 12,
        },
        axisLabel: {
          fontSize: 11,
          color: "#666",
        },
        axisLine: {
          lineStyle: {
            color: "#e9ecef",
          },
        },
        splitLine: {
          lineStyle: {
            color: "#f1f3f4",
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: "Time Spent",
          type: "bar",
          data: chartData.hours.map((value, index) => ({
            value,
            itemStyle: {
              color: chartData.colors[index],
              borderRadius: [4, 4, 0, 0],
            },
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.2)",
            },
          },
          barWidth: "60%",
        },
      ],
    };
  }, [chartData]);

  if (isLoading) {
    return (
      <ExternalOrgTimeCardWrapper>
        <div className="loader">
          <Loader size="sm" />
          <Text mt={8} size="sm" color="dimmed">
            Loading external organization data
          </Text>
        </div>
      </ExternalOrgTimeCardWrapper>
    );
  }

  if (isError) {
    return (
      <ExternalOrgTimeCardWrapper>
        <div className="loader">
          <Text color="red" size="sm">
            Error loading external organization data
          </Text>
        </div>
      </ExternalOrgTimeCardWrapper>
    );
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <ExternalOrgTimeCardWrapper>
        <EmptyCard />
      </ExternalOrgTimeCardWrapper>
    );
  }

  // Calculate summary stats
  const totalHours = Object.values(data).reduce(
    (sum, org) => sum + (org.hours || 0),
    0
  );
  const totalMeetings = Object.values(data).reduce(
    (sum, org) => sum + (org.meetings || 0),
    0
  );
  const totalOrgs = Object.keys(data).length;

  return (
    <ExternalOrgTimeCardWrapper>
      <ReactECharts
        option={option}
        style={{ height: "280px", width: "100%", marginTop: '2rem' }}
        opts={{ renderer: "svg" }}
        onEvents={onEvents}
      />

      <Flex gap={8} mt={12} wrap="wrap" justify="space-between" align="center">
        <Flex gap={8} wrap="wrap">
          <Badge color="blue" leftSection={<IconClock size={12} />}>
            Total: {Math.ceil(totalHours)} hrs
          </Badge>
          <Badge color="green" leftSection={<IconUsers size={12} />}>
            Meetings: {totalMeetings}
          </Badge>
          <Badge color="orange" leftSection={<IconBuilding size={12} />}>
            Organizations: {totalOrgs}
          </Badge>
        </Flex>

        {totalOrgs > 10 && (
          <Text
            size="sm"
            color="blue"
            sx={{
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
              fontWeight: 500,
            }}
            onClick={() => setDrawerOpened(true)}
          >
            See more
          </Text>
        )}
      </Flex>

      <MeetingDistModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        data={modalData}
        meetingDetails={meetingDetails}
        isLoadingMeetings={isLoadingMeetings}
        isErrorMeetings={isErrorMeetings}
        meetingError={meetingError}
      />

      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        title={
          <div>
            <Title order={4}>All External Organizations</Title>
            <Text size="sm" color="dimmed" mt={4}>
              Click on any organization to view meeting details
            </Text>
          </div>
        }
        padding="lg"
        size="800px"
        position="bottom"
        styles={{
          content: { maxHeight: "70vh" },
          body: { height: "100%", display: "flex", flexDirection: "column" },
        }}
      >
        <ExternalOrgTable
          data={fullData || []}
          onRowClick={handleRowClick}
        />
      </Drawer>
    </ExternalOrgTimeCardWrapper>
  );
};

export default ExternalOrgTimeCard;

const ExternalOrgTimeCardWrapper = ({ children }) => {
  return (
    <Paper
      shadow="sm"
      p="md"
      radius="md"
      withBorder
      className="card"
      h="100%"
    >
      <Flex direction="column" sx={{ width: "100%", height: "100%" }}>
        <Flex align="center" mb="md">
          <IconBuilding size={18} style={{ marginRight: 8 }} />
          <Title order={5}>External Organizations</Title>
        </Flex>

        <Flex direction="column" style={{ flex: 1, justifyContent: "space-between" }}>
          {children}
        </Flex>
      </Flex>
    </Paper>
  );
};
